import { formatarDataCurta, linkWhatsAppMensagem } from '../utils';
import {
  chaveGrupo,
  chaveSenha,
  exemplosDeSenha,
  filaDoGrupo,
  formatarSenha,
  nomeDoGrupo,
  ordinal,
} from '../vagas';
import type { GrupoVagas, SuplenteLinha } from '../types';

interface Props {
  grupos: GrupoVagas[];
  fila: SuplenteLinha[];
  telefonePorInscricao: Map<number, string>;
  contagemSenhas: Map<string, number>;
  aoDarVaga: (inscricaoId: number, nome: string) => void;
  aoAbrirPessoa: (inscricaoId: number) => void;
  ocupado: boolean;
}

// Cartões de ocupação + faixas de alerta no topo da lista.
export default function PainelVagas({
  grupos,
  fila,
  telefonePorInscricao,
  contagemSenhas,
  aoDarVaga,
  aoAbrirPessoa,
  ocupado,
}: Props) {
  const comVagas = grupos.filter((g) => g.vagas !== null && g.vagas !== undefined);
  if (comVagas.length === 0) return null;

  const exemplos = exemplosDeSenha(fila);

  return (
    <section className="mb-4">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center text-primary-600">
          <i className="ri-ticket-2-line" aria-hidden />
        </span>
        <h3 className="text-[14px] font-bold text-foreground-950">Vagas e suplentes</h3>
      </div>

      {/* Cartões: rolagem horizontal no celular, lado a lado no computador */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0">
        {comVagas.map((g) => {
          const vagas = Number(g.vagas ?? 0);
          const ocupadas = Number(g.com_vaga ?? 0);
          const pct = vagas > 0 ? Math.min(100, Math.round((ocupadas / vagas) * 100)) : 0;
          const chave = chaveGrupo(g.modalidade_id, g.opcao_id);
          const senhaExemplo = exemplos.get(chave) ?? null;
          const repetidas = (g.senhas_repetidas ?? []).map((n) =>
            formatarSenha(n, senhaExemplo),
          );
          const puladas = (g.senhas_puladas ?? []).map((n) => formatarSenha(n, senhaExemplo));
          const vencidos = g.prazo_vencido ?? [];
          const primeiro = filaDoGrupo(fila, g)[0] ?? null;

          return (
            <div
              key={chave}
              className="w-[268px] shrink-0 rounded-2xl border border-background-200 bg-white p-4 md:w-auto"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 truncate text-[14px] font-bold text-foreground-950">
                  {nomeDoGrupo(g)}
                </p>
                <span className="shrink-0 whitespace-nowrap text-[12px] font-semibold text-foreground-500">
                  {ocupadas}/{vagas}
                </span>
              </div>

              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className={`h-full rounded-full ${pct >= 100 ? 'bg-accent-500' : 'bg-primary-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>

              <p className="mt-2 text-[13px] text-foreground-700">
                {ocupadas}/{vagas} com vaga · {g.suplentes} suplentes · {g.livres ?? 0} livres
              </p>
              <p className="text-[12px] text-foreground-500">
                {g.confirmadas} confirmadas (pagas)
              </p>

              {g.alerta_vaga_livre && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-2.5">
                  <p className="text-[12px] font-medium leading-snug text-amber-900">
                    {g.livres ?? 0} vaga{(g.livres ?? 0) === 1 ? '' : 's'} livre
                    {(g.livres ?? 0) === 1 ? '' : 's'} em {nomeDoGrupo(g)} e suplentes esperando
                  </p>
                  <button
                    type="button"
                    onClick={() => primeiro && aoDarVaga(primeiro.inscricao_id, primeiro.nome)}
                    disabled={ocupado || !primeiro}
                    className="mt-2 min-h-[36px] w-full cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {primeiro
                      ? `Dar a vaga a ${primeiro.nome.split(' ')[0]} (${ordinal(primeiro.posicao)})`
                      : 'Sem suplente na fila'}
                  </button>
                </div>
              )}

              {puladas.length > 0 && (
                <p className="mt-2 rounded-xl bg-background-100 px-2.5 py-2 text-[12px] leading-snug text-foreground-600">
                  {puladas.map((s) => `Senha ${s} não foi usada por ninguém neste grupo`).join(' · ')}
                </p>
              )}

              {repetidas.length > 0 && (
                <p className="mt-2 rounded-xl bg-amber-50 px-2.5 py-2 text-[12px] leading-snug text-amber-900">
                  {repetidas
                    .map((s) => {
                      const qtd = contagemSenhas.get(chaveSenha(chave, s)) ?? 2;
                      return `Senha ${s} aparece em ${qtd} fichas — corrija a senha`;
                    })
                    .join(' · ')}
                </p>
              )}

              {vencidos.map((v) => (
                <div
                  key={v.inscricao_id}
                  className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5"
                >
                  <p className="text-[12px] font-medium leading-snug text-amber-900">
                    {v.nome} subiu de suplente em {formatarDataCurta(v.subiu_em)} e ainda não pagou
                  </p>
                  <div className="mt-2 flex gap-2">
                    {telefonePorInscricao.get(v.inscricao_id) && (
                      <a
                        href={linkWhatsAppMensagem(
                          telefonePorInscricao.get(v.inscricao_id) as string,
                          `Oi, ${v.nome.split(' ')[0]}! Sua vaga no ${g.modalidade} está reservada, mas o pagamento ainda não apareceu. Consegue acertar hoje?`,
                        )}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex min-h-[36px] flex-1 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full bg-white px-3 text-[12px] font-semibold text-amber-900"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-whatsapp-line" aria-hidden />
                        </span>
                        WhatsApp
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => aoAbrirPessoa(v.inscricao_id)}
                      className="flex min-h-[36px] flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-full border border-amber-300 bg-white px-3 text-[12px] font-semibold text-amber-900 transition-colors hover:bg-amber-100"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}